import axiosWithAuth from '../utils/axiosWithAuth';
import api from './api';
import { getApiEndpoint } from '../utils/apiConfig';

export const syncRepairData = async (repairData) => {
  try {
    // Get api instance by calling the axiosWithAuth function
    const api = axiosWithAuth();

    // Validate required data
    if (!repairData.repairStyle || !repairData.repairTariff || !repairData.property) {
      throw new Error('Missing required repair data');
    }

    // Ensure materials is an array before mapping
    const materials = Array.isArray(repairData.materials) ? repairData.materials : [];
    
    // Format materials data
    const formattedMaterials = materials.map(material => ({
      materialId: material.materialId || material.id,
      name: material.name || material.materialName,
      count: Number(material.count || 1),
      area: Number(material.area || 1),
      price: Number(material.price || 0),
      servicePrice: Number(material.servicePrice || 0),
      currency: material.currency || 'AZN',
      type: material.type || 'standard'
    }));

    // Format room data including ensuring proper area values
    const formattedRooms = Array.isArray(repairData.rooms) 
      ? repairData.rooms.map(room => ({
          roomType: room.roomType || room.id,
          name: room.name,
          width: Number(room.width || 0),
          length: Number(room.length || 0),
          area: Number(room.area || 0),
          height: Number(room.height || 0),
          materials: Array.isArray(room.materials) 
            ? room.materials.map(material => ({
                materialId: material.materialId || material.id,
                name: material.name || material.materialName,
                count: Number(material.count || 1),
                area: Number(material.area || room.area || 1),
                price: Number(material.price || 0),
                servicePrice: Number(material.servicePrice || 0),
                currency: material.currency || 'AZN',
                type: material.type || 'standard'
              }))
            : []
        }))
      : [];

    // Get user info for anonymous users
    const getUserInfo = () => {
      try {
        // Try to get from user object first
        if (localStorage.getItem("user")) {
          const userData = JSON.parse(localStorage.getItem("user"));
          if (userData) {
            return {
              userId: userData._id,
              userName: `${userData.name || ''} ${userData.surname || ''}`.trim()
            };
          }
        }
        
        // If no user data, return anonymous info
        return {
          userId: null,
          userName: "Anonymous User",
          isAnonymous: true
        };
      } catch (error) {
        console.error("Error getting user info:", error);
        return { userId: null, userName: "Anonymous User", isAnonymous: true };
      }
    };

    const userInfo = getUserInfo();

    // Prepare property data ensuring it has the right format
    let propertyData = repairData.property;
    
    // If property is a string ID, convert to expected object format
    if (typeof propertyData === 'string') {
      propertyData = { 
        _id: propertyData 
      };
    } 
    // If property is an object with nested IDs, format properly
    else if (typeof propertyData === 'object') {
      // Format propertyType
      if (propertyData.propertyType && typeof propertyData.propertyType === 'object' && propertyData.propertyType.id) {
        propertyData.propertyType = propertyData.propertyType.id;
      }
      
      // Format propertyStatus
      if (propertyData.propertyStatus && typeof propertyData.propertyStatus === 'object' && propertyData.propertyStatus.id) {
        propertyData.propertyStatus = propertyData.propertyStatus.id;
      }
      
      // Add formatted rooms to property
      propertyData.rooms = formattedRooms;
    }

    // Format repairStyle (handle both string ID and object with ID)
    const repairStyle = typeof repairData.repairStyle === 'object' && repairData.repairStyle.id 
      ? repairData.repairStyle.id 
      : repairData.repairStyle;
      
    // Format repairTariff (handle both string ID and object with ID)
    const repairTariff = typeof repairData.repairTariff === 'object' && repairData.repairTariff.id 
      ? repairData.repairTariff.id 
      : repairData.repairTariff;

    // Prepare data for API
    const apiData = {
      repairStyle,
      repairTariff,
      property: propertyData,
      materials: formattedMaterials,
      userProfile: userInfo,
      materialsCost: Number(repairData.materialsCost || 0),
      serviceFee: Number(repairData.serviceCost || 0),
      generalCost: Number(repairData.generalCost || 0),
      duration: Number(repairData.duration || 1)
    };

    console.log('Preparing to send repair data to API:', apiData);

    // Make API call
    const response = await api.post('/repairs', apiData);

    // Update local storage with response data
    if (response.data?.data) {
      const { data } = response.data;
      
      // Store only necessary data
      sessionStorage.setItem('currentRepair', JSON.stringify({
        id: data._id,
        repairStyle: data.repairStyle,
        repairTariff: data.repairTariff,
        property: data.property,
        materialsCost: data.materialsCost,
        serviceFee: data.serviceFee,
        generalCost: data.generalCost
      }));
    }

    return response.data;
  } catch (error) {
    console.error('Error syncing repair data:', error);
    throw error;
  }
};

// Helper function to get current repair data
export const getCurrentRepair = () => {
  try {
    const repairData = sessionStorage.getItem('currentRepair');
    return repairData ? JSON.parse(repairData) : null;
  } catch (error) {
    console.error('Error getting current repair:', error);
    return null;
  }
};

// Helper function to clear repair data
export const clearRepairData = () => {
  try {
    // Get keys to preserve
    const preserveKeys = ['selectedRoom', 'selectedMaterial'];
    
    // Store preserved data
    const preservedData = {};
    preserveKeys.forEach(key => {
      const value = sessionStorage.getItem(key);
      if (value) preservedData[key] = value;
    });
    
    // Clear repair-related data
    const repairKeys = Object.keys(sessionStorage).filter(key => 
      key.startsWith('repair') || 
      key.startsWith('temp-') || 
      key === 'currentRepair'
    );
    
    repairKeys.forEach(key => sessionStorage.removeItem(key));
    
    // Restore preserved data
    Object.entries(preservedData).forEach(([key, value]) => {
      sessionStorage.setItem(key, value);
    });
  } catch (error) {
    console.error('Error clearing repair data:', error);
  }
};

// Function to create breakdown only without creating a repair
export const createBreakdownOnly = async (breakdownData) => {
  try {
    // Get API instance
    const api = axiosWithAuth();
    
    // Make API call
    const response = await api.post('/repairs/breakdown-only', breakdownData);
    
    return response.data;
  } catch (error) {
    console.error('Error creating breakdown:', error);
    throw error;
  }
};

// Function to get breakdown by ID
export const getBreakdown = async (breakdownId) => {
  try {
    // Get API instance
    const api = axiosWithAuth();
    
    // Make API call
    const response = await api.get(`/repairs/breakdown/${breakdownId}`);
    
    return response.data;
  } catch (error) {
    console.error('Error getting breakdown:', error);
    throw error;
  }
};

export const getStylesList = async () => {
  try {
    const response = await api.get('/styles');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getTariffsList = async () => {
  try {
    const response = await api.get('/tariffs');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Calculate a repair breakdown - central function used by Material.jsx and other components
// Update breakdown with new default materials status
export const updateBreakdownDefaults = async (data) => {
  try {
    console.log('Updating breakdown defaults with request:', data);
    
    const response = await axiosWithAuth().put(
      getApiEndpoint('repairs/update-breakdown-defaults'),
      data
    );
    
    console.log('Update breakdown defaults response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating breakdown defaults:', error);
    throw error;
  }
};

export const calculateRepairBreakdown = async (...args) => {
  try {
    // Determine what kind of parameters we got and format accordingly
    let requestData;
    
    // Check if old format (multiple parameters) or new format (single object)
    if (args.length > 1) {
      // Old format: (selectedMaterial, roomData, repairStyleId, repairTariffId, defaultMaterialsStatus)
      const [selectedMaterial, roomData, repairStyleId, repairTariffId, defaultMaterialsStatus = {}] = args;
      
      requestData = {
        repairStyle: repairStyleId,
        repairTariff: repairTariffId,
        property: roomData,
        materials: selectedMaterial,
        defaultMaterialsStatus
      };
    } else {
      // New format: single object with all needed properties
      const data = args[0];
      requestData = {
        repairStyle: data.repairStyle,
        repairTariff: data.repairTariff,
        property: data.property,
        materials: data.materials,
        defaultMaterialsStatus: data.defaultMaterialsStatus || {}
      };
    }
    
    console.log('Calculating repair breakdown with request:', requestData);
    
    // Make the API call
    const response = await axiosWithAuth().post(
      getApiEndpoint('repairs/calculate-breakdown'),
      requestData
    );
    
    return response.data;
  } catch (error) {
    console.error('Error calculating repair breakdown:', error);
    throw error;
  }
};

export const saveBreakdownDirectly = async (propertyId, styleId, tariffId, breakdownData) => {
  try {
    const response = await api.post('/repairs/direct-save-breakdown', {
      propertyId,
      styleId,
      tariffId,
      breakdownData
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getRepairsByPropertyId = async (propertyId) => {
  try {
    const response = await api.get(`/repairs/property/${propertyId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Save repair calculation to database
 * @param {Object} calculationData - Calculation data
 * @returns {Promise} - API response
 */
export const saveRepairCalculation = async (calculationData) => {
  try {
    console.log('Saving repair calculation to database:', {
      tempId: calculationData.tempId,
      hasBreakdown: !!calculationData.breakdown,
      hasCosts: !!calculationData.costs,
      hasSelectedMaterial: !!calculationData.selectedMaterial
    });
    
    const response = await axiosWithAuth().post(
      getApiEndpoint('repairs/calculations'),
      calculationData
    );
    
    console.log('Calculation saved successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error saving repair calculation:', error);
    
    // Save to localStorage as fallback if API call fails
    if (calculationData.tempId) {
      console.log('Saving calculation to localStorage as fallback');
      saveTemporaryCalculation(calculationData.tempId, calculationData);
    }
    
    // Rethrow the error for the component to handle
    throw error;
  }
};

/**
 * Get user's repair calculations
 * @returns {Promise} - API response
 */
export const getRepairCalculations = async () => {
  try {
    const endpoint = `${getApiEndpoint()}/repairs/calculations`;
    console.log('Using endpoint:', endpoint);
    
    const response = await axiosWithAuth().get(endpoint);
    console.log('Get response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching repair calculations:', error);
    if (error.response) {
      console.error('Error response:', error.response.data);
      console.error('Error status:', error.response.status);
    }
    throw error;
  }
};

/**
 * Save temporary calculation to localStorage and sessionStorage
 * @param {string} tempId - Temporary calculation ID
 * @param {Object} data - Calculation data
 */
export const saveTemporaryCalculation = (tempId, data) => {
  try {
    // Save to localStorage
    localStorage.setItem(tempId, JSON.stringify(data));
    
    // Save selected material to sessionStorage
    if (data.selectedMaterial) {
      sessionStorage.setItem('selectedMaterial', JSON.stringify(data.selectedMaterial));
    }
    
    return true;
  } catch (error) {
    console.error('Error saving temporary calculation:', error);
    return false;
  }
};

/**
 * Get temporary calculation from storage
 * @param {string} tempId - Temporary calculation ID
 * @returns {Object|null} - Calculation data or null
 */
export const getTemporaryCalculation = (tempId) => {
  try {
    const data = localStorage.getItem(tempId);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error getting temporary calculation:', error);
    return null;
  }
}; 